import { Injectable } from '@angular/core';
import {
    HttpClient,
    HttpParams,
    HttpErrorResponse,
    HttpHeaders,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';

// DTOs e Interfaces locales (sin dependencias de NestJS)
export interface IOsAgenciaQuery {
    tabType: string;
    page?: number;
    limit?: number;
    orderBy?: string;
    orderDirection?: 'ASC' | 'DESC';
    customerId?: number;
    serviceId?: number;
    vesselId?: number;
    typeOfInvoicingId?: number;
    osNumber?: number;
    shippingInstructionId?: number;
    transportOrderId?: number;
    shippingLiquidationId?: number;
    bookingNumber?: string;
    containerNumber?: string;
    dusNumber?: string;
    temporadaId?: number;
    fromDate?: string;
    toDate?: string;
}

export interface IOsAgenciaTotals {
    totalVentaNeto: number;
    totalTraspasoBruto: number;
    totalGeneral: number;
}

export interface IOsAgenciaPaginatedResponse {
    data: any[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
    tabSummary: string;
    appliedFilters: any;
    totals: IOsAgenciaTotals;
}

export interface OsAgenciaExcelResponseDto {
    total: number;
    tabType: string;
    data: any[];
    generatedAt: Date;
}

export interface IServiceFilter {
    serviceId: number;
    serviceName: string;
}

export interface IVesselFilter {
    vesselId: number;
    vesselName: string;
}

export interface IInvoicingTypeFilter {
    typeOfInvoicingId: number;
    typeOfInvoicingName: string;
}

export interface ISeasonFilter {
    seasonId: number;
    seasonName: string;
    seasonFrom?: string;
    seasonTo?: string;
}

export interface IOsAgenciaDetail {
    clientReference: number;
    voyage: string;
    pol: string;
    etd: string;
    pod: string;
    serviceName: string;
    billingType: string;
    netSale: number;
    grossTransfer: number;
    providerName: string;
    invoiceNumber: string;
    containerNumber: string;
    clientComments: string;
    liquidationDate: string;
    liquidationNumber: number;
}

// Servicios
import { LoggerService } from '../core/services/logger.service';
import { environment } from '../../environments/environment';

/**
 * Servicio para gestionar Órdenes de Servicio Agencia
 * Maneja la comunicación con el backend y proporciona métodos para:
 * - Listado con filtros y paginación
 * - Exportación a Excel
 * - Descarga de documentos
 */
@Injectable({
    providedIn: 'root',
})
export class OsAgenciaService {
    private readonly baseUrl = `${environment.apiUrl}/v1/os-agencia`;

    constructor(private http: HttpClient, private logger: LoggerService) {}

    private getAuthToken(): string | null {
        return (
            localStorage.getItem('access_token') ||
            localStorage.getItem('token')
        );
    }

    private getAuthHeaders(): HttpHeaders {
        const token = this.getAuthToken();
        return new HttpHeaders({
            Authorization: token ? `Bearer ${token}` : '',
        });
    }

    /**
     * Obtiene lista paginada de órdenes de servicio agencia
     * Soporta filtros múltiples y tres tipos de tabs
     */
    getOrdenes(
        query: IOsAgenciaQuery
    ): Observable<IOsAgenciaPaginatedResponse> {
        this.logger.info(
            '🔄 OsAgenciaService: Obteniendo órdenes con query:',
            JSON.stringify(query)
        );

        // Construir parámetros HTTP
        let params = new HttpParams();

        // Parámetro obligatorio
        params = params.set('tabType', query.tabType);

        // Paginación
        if (query.page) {
            params = params.set('page', query.page.toString());
        }
        if (query.limit) {
            params = params.set('limit', query.limit.toString());
        }

        // Ordenamiento
        if (query.orderBy) {
            params = params.set('orderBy', query.orderBy);
        }
        if (query.orderDirection) {
            params = params.set('orderDirection', query.orderDirection);
        }

        // Filtros opcionales
        if (query.customerId) {
            params = params.set('customerId', query.customerId.toString());
        }
        if (query.serviceId) {
            params = params.set('serviceId', query.serviceId.toString());
        }
        if (query.vesselId) {
            params = params.set('vesselId', query.vesselId.toString());
        }
        if (query.typeOfInvoicingId) {
            params = params.set(
                'typeOfInvoicingId',
                query.typeOfInvoicingId.toString()
            );
        }
        if (query.osNumber) {
            params = params.set('osNumber', query.osNumber.toString());
        }
        if (query.shippingInstructionId) {
            params = params.set(
                'shippingInstructionId',
                query.shippingInstructionId.toString()
            );
        }
        if (query.transportOrderId) {
            params = params.set(
                'transportOrderId',
                query.transportOrderId.toString()
            );
        }
        if (query.shippingLiquidationId) {
            params = params.set(
                'shippingLiquidationId',
                query.shippingLiquidationId.toString()
            );
        }
        if (query.bookingNumber?.trim()) {
            params = params.set('bookingNumber', query.bookingNumber.trim());
        }
        if (query.containerNumber?.trim()) {
            params = params.set(
                'containerNumber',
                query.containerNumber.trim()
            );
        }
        if (query.dusNumber?.trim()) {
            params = params.set('dusNumber', query.dusNumber.trim());
        }
        if (query.temporadaId) {
            params = params.set('temporadaId', query.temporadaId.toString());
        }
        if (query.fromDate) {
            params = params.set('fromDate', query.fromDate);
        }
        if (query.toDate) {
            params = params.set('toDate', query.toDate);
        }

        return this.http
            .get<IOsAgenciaPaginatedResponse>(this.baseUrl, {
                params,
                headers: this.getAuthHeaders(),
            })
            .pipe(
                retry(2), // Reintentar hasta 2 veces en caso de error
                catchError((error: HttpErrorResponse) => {
                    this.logger.error(
                        '❌ Error obteniendo órdenes de servicio agencia:',
                        error
                    );
                    return this.handleError(error);
                })
            );
    }

    /**
     * Obtiene datos para exportación a Excel
     * @param query Filtros y tipo de tab para la exportación
     */
    getExcelData(
        query: IOsAgenciaQuery
    ): Observable<OsAgenciaExcelResponseDto> {
        this.logger.info(
            '📊 OsAgenciaService: Obteniendo datos para Excel:',
            JSON.stringify(query)
        );

        // Construir parámetros HTTP similares a getOrdenes pero para Excel
        let params = new HttpParams();
        params = params.set('tabType', query.tabType);

        // Filtros opcionales (misma lógica que getOrdenes)
        if (query.customerId) {
            params = params.set('customerId', query.customerId.toString());
        }
        if (query.serviceId) {
            params = params.set('serviceId', query.serviceId.toString());
        }
        if (query.vesselId) {
            params = params.set('vesselId', query.vesselId.toString());
        }
        if (query.typeOfInvoicingId) {
            params = params.set(
                'typeOfInvoicingId',
                query.typeOfInvoicingId.toString()
            );
        }
        if (query.osNumber) {
            params = params.set('osNumber', query.osNumber.toString());
        }
        if (query.shippingInstructionId) {
            params = params.set(
                'shippingInstructionId',
                query.shippingInstructionId.toString()
            );
        }
        if (query.transportOrderId) {
            params = params.set(
                'transportOrderId',
                query.transportOrderId.toString()
            );
        }
        if (query.shippingLiquidationId) {
            params = params.set(
                'shippingLiquidationId',
                query.shippingLiquidationId.toString()
            );
        }
        if (query.bookingNumber?.trim()) {
            params = params.set('bookingNumber', query.bookingNumber.trim());
        }
        if (query.containerNumber?.trim()) {
            params = params.set(
                'containerNumber',
                query.containerNumber.trim()
            );
        }
        if (query.dusNumber?.trim()) {
            params = params.set('dusNumber', query.dusNumber.trim());
        }
        if (query.temporadaId) {
            params = params.set('temporadaId', query.temporadaId.toString());
        }
        if (query.fromDate) {
            params = params.set('fromDate', query.fromDate);
        }
        if (query.toDate) {
            params = params.set('toDate', query.toDate);
        }

        const excelUrl = `${this.baseUrl}/excel-data`;

        return this.http
            .get<OsAgenciaExcelResponseDto>(excelUrl, {
                params,
                headers: this.getAuthHeaders(),
            })
            .pipe(
                retry(1), // Solo 1 reintento para Excel
                catchError((error: HttpErrorResponse) => {
                    this.logger.error(
                        '❌ Error obteniendo datos Excel para órdenes de servicio agencia:',
                        error
                    );
                    return this.handleError(error);
                })
            );
    }

    /**
     * Descarga PDF consolidado para una orden de servicio
     * @param serviceOrderId ID de la orden de servicio
     */
    downloadConsolidatedPdf(serviceOrderId: number): Observable<Blob> {
        this.logger.info(
            `📄 OsAgenciaService: Descargando PDF consolidado para OS ID: ${serviceOrderId}`
        );

        const pdfUrl = `${environment.apiUrl}/v1/os-agencia/${serviceOrderId}/pdf`;

        return this.http
            .get(pdfUrl, {
                responseType: 'blob',
                headers: new HttpHeaders({
                    Accept: 'application/pdf',
                    Authorization: this.getAuthToken()
                        ? `Bearer ${this.getAuthToken()}`
                        : '',
                }),
            })
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    this.logger.error(
                        `❌ Error descargando PDF para OS ${serviceOrderId}:`,
                        error
                    );
                    return this.handleError(error);
                })
            );
    }

    /**
     * Obtiene el detalle consolidado de una orden de servicio
     * @param serviceOrderId Número de orden de servicio
     */
    getOrderDetail(serviceOrderId: number): Observable<IOsAgenciaDetail> {
        this.logger.info(
            `📄 OsAgenciaService: Obteniendo detalle para OS ID: ${serviceOrderId}`
        );

        const detailUrl = `${this.baseUrl}/${serviceOrderId}/detail`;

        return this.http
            .get<IOsAgenciaDetail>(detailUrl, {
                headers: this.getAuthHeaders(),
            })
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    this.logger.error(
                        `❌ Error obteniendo detalle para OS ${serviceOrderId}:`,
                        error
                    );
                    return this.handleError(error);
                })
            );
    }

    /**
     * Obtiene lista de archivos disponibles para una orden de servicio
     * @param serviceOrderId ID de la orden de servicio
     */
    getOrderFiles(serviceOrderId: number): Observable<any[]> {
        this.logger.info(
            `📁 OsAgenciaService: Obteniendo archivos para OS ID: ${serviceOrderId}`
        );

        const filesUrl = `${environment.apiUrl}/v1/os-agencia/${serviceOrderId}/files`;

        return this.http
            .get<any[]>(filesUrl, { headers: this.getAuthHeaders() })
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    this.logger.error(
                        `❌ Error obteniendo archivos para OS ${serviceOrderId}:`,
                        error
                    );
                    return this.handleError(error);
                })
            );
    }

    /**
     * Manejo centralizado de errores HTTP
     * @param error Error HTTP recibido
     */
    private handleError(error: HttpErrorResponse): Observable<never> {
        let errorMessage = 'Error desconocido';

        if (error.error instanceof ErrorEvent) {
            // Error del lado del cliente
            errorMessage = `Error del cliente: ${error.error.message}`;
        } else {
            // Error del lado del servidor
            switch (error.status) {
                case 400:
                    errorMessage = 'Parámetros de consulta inválidos';
                    break;
                case 401:
                    errorMessage =
                        'No autorizado. Por favor, inicie sesión nuevamente';
                    break;
                case 403:
                    errorMessage =
                        'No tiene permisos para acceder a este recurso';
                    break;
                case 404:
                    errorMessage = 'Recurso no encontrado';
                    break;
                case 500:
                    errorMessage = 'Error interno del servidor';
                    break;
                case 503:
                    errorMessage = 'Servicio no disponible temporalmente';
                    break;
                default:
                    errorMessage = `Error del servidor: ${error.status} - ${error.message}`;
            }

            // Intentar extraer mensaje específico del backend
            if (error.error?.message) {
                errorMessage = error.error.message;
            }
        }

        this.logger.error(
            '🚨 Error en OsAgenciaService:',
            new Error(errorMessage)
        );

        return throwError(() => new Error(errorMessage));
    }

    /**
     * Verifica si el servicio está disponible
     */
    checkHealth(): Observable<{ status: string; timestamp: string }> {
        const healthUrl = `${environment.apiUrl}/v1/health`;

        return this.http
            .get<{ status: string; timestamp: string }>(healthUrl)
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    this.logger.error(
                        '❌ Error verificando salud del servicio:',
                        error
                    );
                    return throwError(
                        () => new Error('Servicio no disponible')
                    );
                })
            );
    }

    /**
     * Obtiene la lista de servicios para filtros
     */
    getServicesFilter(): Observable<IServiceFilter[]> {
        return this.http
            .get<IServiceFilter[]>(`${this.baseUrl}/filters/services`, {
                headers: this.getAuthHeaders(),
            })
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    this.logger.error(
                        '❌ Error obteniendo servicios para filtros:',
                        error
                    );
                    return this.handleError(error);
                })
            );
    }

    /**
     * Obtiene la lista de naves para filtros
     */
    getVesselsFilter(): Observable<IVesselFilter[]> {
        return this.http
            .get<IVesselFilter[]>(`${this.baseUrl}/filters/vessels`, {
                headers: this.getAuthHeaders(),
            })
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    this.logger.error(
                        '❌ Error obteniendo naves para filtros:',
                        error
                    );
                    return this.handleError(error);
                })
            );
    }

    /**
     * Obtiene la lista de tipos de facturación para filtros
     */
    getInvoicingTypesFilter(): Observable<IInvoicingTypeFilter[]> {
        return this.http
            .get<IInvoicingTypeFilter[]>(
                `${this.baseUrl}/filters/invoicing-types`,
                { headers: this.getAuthHeaders() }
            )
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    this.logger.error(
                        '❌ Error obteniendo tipos de facturación para filtros:',
                        error
                    );
                    return this.handleError(error);
                })
            );
    }

    /**
     * Obtiene la lista de temporadas para filtros
     * La primera temporada es la más reciente (por defecto)
     */
    getSeasonsFilter(): Observable<ISeasonFilter[]> {
        return this.http
            .get<ISeasonFilter[]>(`${this.baseUrl}/filters/seasons`, {
                headers: this.getAuthHeaders(),
            })
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    this.logger.error(
                        '❌ Error obteniendo temporadas para filtros:',
                        error
                    );
                    return this.handleError(error);
                })
            );
    }

    /**
     * Utilidad para construir filtros desde un objeto de filtros parcial
     * @param filters Filtros parciales
     */
    buildQuery(
        tabType: string,
        filters: Partial<IOsAgenciaQuery> = {},
        pagination: { page?: number; limit?: number } = {},
        sorting: { orderBy?: string; orderDirection?: 'ASC' | 'DESC' } = {}
    ): IOsAgenciaQuery {
        return {
            tabType: tabType as any,
            page: pagination.page || 1,
            limit: pagination.limit || 50,
            orderBy: sorting.orderBy || 'creationDate',
            orderDirection: sorting.orderDirection || 'DESC',
            ...filters,
        };
    }
}
